import { Link, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import type { Permission } from '@/types';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NavItem {
  name: string;
  href?: string;
  children?: { name: string; href: string; permission?: Permission }[];
  permission?: Permission;
}

const navigation: NavItem[] = [
  { name: 'Leads', href: '/leads', permission: Permissions.LeadsRead },
  // "My Account" is intentionally not a top-level nav item — it is only
  // relevant to project managers checking their own commissions, so it lives
  // in the user dropdown below rather than taking space in the main nav
  // (Angie, Note 2).
  {
    name: 'Personnel',
    children: [
      { name: 'Subcontractors', href: '/subcontractors', permission: Permissions.SubcontractorsRead },
      { name: 'Users', href: '/users', permission: Permissions.UsersUpdate },
    ],
  },
  {
    name: 'Admin',
    permission: Permissions.SettingsUpdate,
    children: [
      { name: 'Estimator Accounts', href: '/estimator-accounts', permission: Permissions.UsersUpdate },
      { name: 'Reports', href: '/reports', permission: Permissions.UsersUpdate },
      { name: 'Lookups', href: '/admin/lookups', permission: Permissions.SettingsUpdate },
      { name: 'System Settings', href: '/admin/settings', permission: Permissions.SettingsUpdate },
    ],
  },
];

/**
 * Application header with horizontal navigation
 * Matches Figma design with Atlas logo, nav items, and user dropdown
 */
export function Header() {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter((item) => {
    // If no permission required, show it
    if (!item.permission) return true;
    // Otherwise check if user has the permission
    return hasPermission(item.permission);
  }).map((item) => {
    // Filter children based on permissions too
    if (item.children) {
      return {
        ...item,
        children: item.children.filter((child) => {
          if (!child.permission) return true;
          return hasPermission(child.permission);
        }),
      };
    }
    return item;
  }).filter((item) => {
    // Remove dropdown items with no children after filtering
    if (item.children && item.children.length === 0) return false;
    return true;
  });

  const isNavItemActive = (item: NavItem): boolean => {
    if (item.href) {
      return (
        location.pathname === item.href ||
        (item.href === '/leads' && location.pathname === '/')
      );
    }
    if (item.children) {
      return item.children.some((child) => {
        // Special handling for admin routes
        if (child.href.startsWith('/admin/')) {
          return location.pathname.startsWith(child.href);
        }
        return location.pathname.startsWith(child.href);
      });
    }
    return false;
  };

  return (
    // Padding matches the page container below (px-5) and the nav is no longer
    // capped at max-w-7xl. The header used to be centred inside 1280px while
    // page content ran full width, so on a wide screen the user menu stopped
    // ~300px short of the right edge the content reached.
    <header className="w-full bg-background px-5 py-2 shadow-sm">
      <div className="flex items-center justify-between">
          {/* Left: Atlas Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img
                src="/assets/images/atlas-logo.png"
                alt="Atlas Restoration"
                className="h-9 w-auto"
              />
            </Link>
          </div>

          {/* Center: Horizontal Navigation */}
          <nav className="flex items-center gap-2">
            {filteredNavigation.map((item) => {
              const isActive = isNavItemActive(item);

              // Dropdown menu for items with children
              if (item.children) {
                return (
                  <DropdownMenu key={item.name}>
                    <DropdownMenuTrigger
                      className={cn(
                        'flex cursor-pointer items-center gap-1 rounded-md px-5 py-1.5 text-base font-semibold transition-colors',
                        isActive
                          ? 'text-white'
                          : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                      )}
                      style={isActive ? { backgroundColor: 'hsl(var(--primary))' } : undefined}
                    >
                      {item.name}
                      <ChevronDown className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-48">
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.name} asChild>
                          <Link
                            to={child.href}
                            className={cn(
                              'cursor-pointer',
                              location.pathname.startsWith(child.href) &&
                                'font-semibold'
                            )}
                          >
                            {child.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              // Simple link for items without children
              return (
                <Link
                  key={item.name}
                  to={item.href!}
                  className={cn(
                    'cursor-pointer rounded-md px-5 py-1.5 text-base font-semibold transition-colors',
                    isActive
                      ? 'text-white'
                      : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                  )}
                  style={isActive ? { backgroundColor: 'hsl(var(--primary))' } : undefined}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Right: User Dropdown */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex cursor-pointer items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-gray-100">
                <span>{user.firstName?.charAt(0)}{user.lastName?.toLowerCase()}</span>
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/account" className="cursor-pointer">
                    My Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
      </div>
    </header>
  );
}
